import { addDays, endOfMonth, format, isBefore, parse, startOfDay, startOfMonth, subMonths } from 'date-fns';
import { cs, de, enUS, es, fr, it, pt, type Locale } from 'date-fns/locale';

/**
 * Parse date string safely for Safari compatibility.
 * Safari requires proper ISO format, so we normalize common API date formats.
 *
 * Handles:
 * - "YYYY-MM-DD HH:mm:ss UTC" -> "YYYY-MM-DDTHH:mm:ss.000Z"
 * - Standard ISO strings
 * - Other date formats (fallback)
 */
export const parseDateSafely = (dateString: string): Date => {
	if (!dateString) return new Date(NaN);

	// Safari requires proper ISO format, convert "YYYY-MM-DD HH:mm:ss UTC" to "YYYY-MM-DDTHH:mm:ss.000Z"
	const normalizedDate = dateString.replace(' UTC', '.000Z').replace(' ', 'T');
	const date = new Date(normalizedDate);

	// Fallback to original string if normalized parsing fails
	if (isNaN(date.getTime())) {
		return new Date(dateString);
	}

	return date;
};

/**
 * Parse a UTC date string and return a local Date preserving the calendar date.
 * Avoids timezone offset shifting midnight UTC dates to the previous day.
 * e.g. "2025-08-06 00:00:00 UTC" -> Aug 6 at noon local time (not Aug 5 at 5pm PDT)
 */
export const parseUTCDateAsLocal = (dateString: string): Date => {
	const parsed = parseDateSafely(dateString);

	if (isNaN(parsed.getTime())) return parsed;

	return new Date(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate(), 12, 0, 0, 0);
};

/**
 * Check if a date is valid (not NaN/Invalid Date)
 */
export const isValidDate = (date: Date): boolean => {
	return !isNaN(date.getTime());
};

/**
 * Get month/year string like "January 2025" from a date string.
 * Returns "Unknown Date" for invalid dates.
 */
export const getMonthYear = (dateString: string): string => {
	const date = parseDateSafely(dateString);

	if (!isValidDate(date)) {
		return 'Unknown Date';
	}

	return format(date, 'MMMM yyyy');
};

/**
 * Format a date string safely, returning a fallback for invalid dates.
 */
export const formatDateSafely = (dateString: string, formatStr: string, fallback = 'Unknown Date'): string => {
	const date = parseDateSafely(dateString);

	if (!isValidDate(date)) {
		return fallback;
	}

	return format(date, formatStr);
};

export function toTimelessDateString(date: Date): string;
export function toTimelessDateString(date: null | undefined): null;
export function toTimelessDateString(date?: Date | null): string | null;
export function toTimelessDateString(date?: Date | null): string | null {
	if (!date) {
		return null;
	}
	return format(date, 'yyyy-MM-dd');
}

export const dateIsBeforeToday = (date: Date) => {
	return isBefore(startOfDay(date), startOfDay(new Date()));
};

export const dateIsBeforeTomorrow = (date: Date) => {
	return isBefore(startOfDay(date), startOfDay(addDays(new Date(), 1)));
};

const dateFnsLocaleMap: Record<string, Locale> = {
	cs,
	de,
	es,
	fr,
	it,
	pt,
};

/**
 * Map an i18n language code (e.g. from `i18n.language`) to the corresponding
 * `date-fns` Locale object.  Falls back to `enUS` for unknown codes.
 */
export const getDateFnsLocale = (language: string): Locale => {
	return dateFnsLocaleMap[language] ?? enUS;
};

// NOTE month is in format MM-YYYY (03-2025 is March 2025)
// and we want to convert it to a date object
export const convertMonthToDate = (monthYear: string) => {
	const [monthPart, yearPart] = monthYear.split('-');
	const year = parseInt(yearPart);
	const month = parseInt(monthPart);
	return new Date(year, month - 1, 1);
};

/**
 * Format used for `start_date` / `end_date` query params on monthly date pickers.
 */
export const URL_DATE_FORMAT = 'yyyy-MM-dd';

/**
 * Default start of the previous full month — used as the default `start_date`
 * for monthly report date pickers when no value is present in the URL.
 */
export const defaultLastMonthStart = () => startOfMonth(subMonths(new Date(), 1));

/**
 * Default end of the previous full month — used as the default `end_date`
 * for monthly report date pickers when no value is present in the URL.
 */
export const defaultLastMonthEnd = () => endOfMonth(subMonths(new Date(), 1));

/**
 * Parse a `URL_DATE_FORMAT` string from the URL, falling back to `fallback()`
 * when the value is missing or invalid.
 */
export const parseUrlDate = (value: string | null, fallback: () => Date): Date => {
	if (!value) return fallback();
	const parsed = parse(value, URL_DATE_FORMAT, new Date());
	return Number.isNaN(parsed.getTime()) ? fallback() : parsed;
};
